import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { storage } from '../../firebaseConfig';
import SignatureCanvasImport from 'react-signature-canvas';
import './SignEngagementLetter.css';
import { captureException } from '@sentry/react';
import { LoadingView } from '../../components/LoadingView';
import { telemetry } from '@/lib/telemetry';
import { invokeBackendRoute } from '@/backend/invoke';
import { Group, Alert, Text, Button, Stack, Tooltip } from '@mantine/core';
import type { GetEngagementLetter } from '@cloudFunctions/types/engagementLetters/getEngagementLetter';
import { composeEngagementLetterPdf } from './composeEngagementLetterPdf';
import { SaveDialog } from './SaveDialog';
import DOMPurify from 'dompurify';
import {
  CheckCircle2Icon,
  FileSignatureIcon,
  PenBoxIcon,
  PenIcon,
  SaveIcon,
  SignatureIcon,
  Undo2Icon,
  UndoIcon,
} from 'lucide-react';

interface EngagementLetterParams extends Record<string, string | undefined> {
  letterId: string;
  matterId: string;
  formattedName: string;
  firmId: string;
}

// `react-signature-canvas` is CommonJS; under @vitejs/plugin-react v6 (Oxc) the
// default-import shape can resolve to `{ default: Component }` instead of the
// component itself, which crashes React with error #130. Coerce defensively so
// the import works regardless of interop mode.
const SignatureCanvas = ((
  SignatureCanvasImport as unknown as {
    default?: typeof SignatureCanvasImport;
  }
).default ?? SignatureCanvasImport) as typeof SignatureCanvasImport;

const SignEngagementLetter = () => {
  const { letterId, matterId, formattedName, firmId } =
    useParams<EngagementLetterParams>();
  const [letterData, setLetterData] = useState<
    { logoUrl: string; text: string; textBlocks: string[] } | undefined
  >();
  const [signatureStatus, setSignatureStatus] = useState<
    'unsigned' | 'signed' | 'saving' | 'saved'
  >('unsigned');
  const abortControllerRef = useRef<AbortController | undefined>();
  const sigCanvas = useRef<SignatureCanvasImport | undefined>();
  const [timeStamp, setTimeStamp] = useState<string | undefined>();
  const [loading, setLoading] = useState(true); // State for loading status
  const [pdfUrl, setPdfUrl] = useState<string | undefined>();
  const [pctSaved, setPctSaved] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  let specificClientName = 'Client'; // Default value

  // Check if formattedName is defined before trying to split it
  if (formattedName) {
    const [formattedFirstName, formattedLastName] = formattedName.split('_');
    if (formattedFirstName && formattedLastName) {
      specificClientName = `${formattedFirstName} ${formattedLastName}`;
    }
  }

  const analyticsPayload = useMemo(() => {
    return {
      signatureStatus: signatureStatus,
      letterId: letterId,
      matterId: matterId,
      formattedName: formattedName,
      firmId: firmId,
    };
  }, [signatureStatus, letterId, matterId, formattedName, firmId]);

  useEffect(() => {
    telemetry.analyticsEvent({
      name: 'engagement_letter_signature_status_change',
      extra: analyticsPayload,
    });
  }, [signatureStatus]);

  useEffect(() => {
    // Add an event to the engagement letter object so that we can tell the
    // attorney whether this page has been visited. Telemetry-only — the
    // response is ignored. Swallow errors (common on iOS Mobile Safari with
    // flaky networks) so they don't surface as unhandled rejections /
    // `FirebaseError: internal` in Sentry.
    invokeBackendRoute<unknown, { firmId?: string; letterId?: string }>(
      'recordSignaturePortalView',
      {
        firmId: firmId,
        letterId: letterId,
      },
    ).catch((error) => {
      telemetry.warn({
        message: 'Failed to record signature portal view',
        extra: { error: String(error), firmId, letterId },
      });
    });
  }, []);

  const handleClearClick = () => {
    telemetry.analyticsEvent({
      name: 'engagement_letter_signature_clear_click',
      extra: analyticsPayload,
    });

    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setSignatureStatus('unsigned');
    }
  };

  const handleSaveSignatureClick = async () => {
    telemetry.analyticsEvent({
      name: 'engagement_letter_signature_save_click',
      extra: analyticsPayload,
    });

    if (signatureStatus != 'signed') {
      return;
    }
    setSignatureStatus('saving');
    const newTimeStamp = new Date().toLocaleString();
    setTimeStamp(newTimeStamp);

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current!.signal;
    setPctSaved(0);

    const progressAfterUpload = 90; // 90% done when letter has been uploaded.
    const progressAfterSave = 99; // 99% done when letter info is saved to firestore

    const pdf = await composeEngagementLetterPdf(
      firmId,
      letterData?.textBlocks ?? [],
      sigCanvas.current?.toDataURL('image/png') ?? '',
      newTimeStamp, // Pass the timestamp to include in the PDF
    );

    telemetry.analyticsEvent({
      name: 'engagement_letter_pdf_rendered',
      extra: analyticsPayload,
    });

    if (signal.aborted) {
      return;
    }

    if (!pdf) {
      console.warn('No PDF found');
      return;
    }

    const getPdfBlob = async () => {
      return new Promise<Blob>((resolve, reject) => {
        pdf.getBlob((blob) => {
          resolve(blob);
        });
      });
    };

    const blob = await getPdfBlob();

    const letterStorageRef = storage.ref(`signedLetters/${letterId}.pdf`);
    const snapshot = await letterStorageRef.put(blob);
    telemetry.analyticsEvent({
      name: 'engagement_letter_pdf_saved_to_bucket',
      extra: analyticsPayload,
    });
    if (signal.aborted) {
      return;
    }
    const downloadUrl = await snapshot.ref.getDownloadURL();
    if (signal.aborted) {
      return;
    }
    setPctSaved(progressAfterUpload);

    await invokeBackendRoute<
      unknown,
      {
        firmId?: string;
        matterId?: string;
        letterUrl: string;
        letterId?: string;
        timestamp: string;
      }
    >('saveEngagementLetterSignature', {
      firmId: firmId,
      matterId: matterId,
      letterUrl: downloadUrl,
      letterId: letterId,
      timestamp: newTimeStamp,
    });
    telemetry.analyticsEvent({
      name: 'engagement_letter_saved_to_firestore',
      extra: {
        ...analyticsPayload,
        downloadUrl,
        timestamp: newTimeStamp,
      },
    });
    if (signal.aborted) {
      return;
    }
    setPctSaved(progressAfterSave);
    setPdfUrl(downloadUrl);
    setSignatureStatus('saved');
  };

  const onEnd = () => {
    if (sigCanvas.current) {
      const isCanvasBlank = sigCanvas.current.isEmpty();
      setSignatureStatus(isCanvasBlank ? 'unsigned' : 'signed');
    }
  };

  const handleSaveACopyClick = () => {
    telemetry.analyticsEvent({
      name: 'engagement_letter_save_copy_click',
      extra: {
        ...analyticsPayload,
        pdfUrl,
      },
    });
    open(pdfUrl, '_blank');
  };

  useEffect(() => {
    invokeBackendRoute<
      GetEngagementLetter.Result,
      { firmId?: string; letterId?: string }
    >('getEngagementLetter', { firmId: firmId, letterId: letterId })
      .then((response) => {
        if (response.status === 'expired') {
          setErrorMessage(response.userMessage);
          return;
        }

        if (response.status === 'success' && response.payload.text) {
          const text = response.payload.text;

          // Check if this is HTML content (new system) or plain text (old system)
          const hasHtmlTags = /<[^>]+>/.test(text);

          let blocks: string[];
          if (hasHtmlTags) {
            // New system: HTML content
            // Remove template source comments first
            const cleanText = text.replace(/<!--[\s\S]*?-->/g, '').trim();

            // Try multiple splitting strategies to ensure we don't lose content

            // Strategy 1: Split by double newlines (from template joining)
            let possibleBlocks = cleanText.split('\n\n');

            // Strategy 2: If that didn't work well, try splitting by double <br> tags
            if (possibleBlocks.length === 1) {
              possibleBlocks = cleanText.split(/<br\s*\/?>\s*<br\s*\/?>/gi);
            }

            // Strategy 3: If still one block, try splitting at section headers
            if (possibleBlocks.length === 1) {
              // Split before <strong> tags (section headers), but keep the <strong> tag with its section
              possibleBlocks = cleanText.split(
                /(?=<strong[^>]*>[^<]+<\/strong>)/gi,
              );

              // Remove empty first element if present
              if (
                possibleBlocks.length > 0 &&
                possibleBlocks[0].trim() === ''
              ) {
                possibleBlocks.shift();
              }
            }

            // Clean up and filter blocks
            blocks = possibleBlocks
              .map((block) => block.trim())
              .filter((block) => block.length > 0);

            // Safety check: if we somehow have no blocks, use the entire text
            if (blocks.length === 0) {
              blocks = [cleanText];
            }
          } else {
            // Old system: plain text, split by double newlines
            blocks = text
              .split('\n\n')
              .filter((block) => block.trim().length > 0);
          }

          // Ensure we have at least one block
          if (blocks.length === 0) {
            blocks = [text];
          }

          setLetterData({
            logoUrl: response.payload.logoUrl,
            text,
            textBlocks: blocks,
          });
        } else {
          console.error('No engagement letter found.');
          captureException(
            new Error('No engagement letter found.'),
            (scope) => {
              scope.setExtras({ ...analyticsPayload });
              return scope;
            },
          );
        }
      })
      .catch((err) => {
        console.error('Failed to load engagement letter:', err);
        setErrorMessage(
          'This engagement letter link has expired or is no longer available. Please contact your firm for a new link.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [firmId, letterId]);

  if (loading) {
    return <LoadingView />;
  }
  if (errorMessage) {
    return (
      <div className='sign-engagement-letter-container'>
        <Group justify='center'>
          <Alert color='red' variant='light' title='Link expired'>
            <Text>{errorMessage}</Text>
          </Alert>
        </Group>
      </div>
    );
  }

  const handleCancelClick = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = undefined;
    setPctSaved(0);
    setSignatureStatus('signed');
  };

  return (
    <>
      <div className='sign-engagement-letter-container'>
        <div className='sign-engagement-letter-content'>
          <Group justify='center'>
            <img
              src={letterData?.logoUrl ?? ''}
              alt='Law Firm Logo'
              className='sign-engagement-letter-logo'
            />
          </Group>
          <h1>Engagement Letter</h1>
          {(letterData?.textBlocks ?? []).map((block, idx) => {
            // Sanitize HTML to prevent XSS attacks while allowing basic formatting
            const sanitizedHtml = DOMPurify.sanitize(block, {
              ALLOWED_TAGS: [
                'strong',
                'b',
                'em',
                'i',
                'u',
                'br',
                'p',
                'div',
                'span',
              ],
              ALLOWED_ATTR: [],
              KEEP_CONTENT: true,
            });

            return (
              <div
                key={idx}
                style={{
                  whiteSpace: 'pre-line',
                  breakBefore: 'page',
                  marginBottom: '1em',
                  lineHeight: '1.5',
                }}
                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
              />
            );
          })}
          {['unsigned', 'signed'].includes(signatureStatus) ? (
            <Stack gap='0' maw='480px'>
              <div className='signature-field'>
                <SignatureCanvas
                  penColor='black'
                  backgroundColor='#fff'
                  canvasProps={{
                    className: 'signature-canvas',
                  }}
                  ref={sigCanvas as any}
                  onEnd={onEnd}
                />
              </div>
              <Group gap='xs' justify='space-between' px='md'>
                <Tooltip
                  label='Please sign your name above before saving'
                  disabled={signatureStatus == 'signed'}
                >
                  <Button
                    className='save-signature-button'
                    onClick={() => {
                      handleSaveSignatureClick();
                    }}
                    leftSection={<FileSignatureIcon size={16} />}
                    disabled={signatureStatus != 'signed'}
                  >
                    Save Signature
                  </Button>
                </Tooltip>
                {signatureStatus == 'signed' && (
                  <Tooltip label='Clear signature'>
                    <Button
                      className='clear-signature-button'
                      variant='subtle'
                      styles={{
                        label: { color: 'var(--mantine-color-gray-8)' },
                      }}
                      leftSection={<Undo2Icon size={16} />}
                      onClick={handleClearClick}
                    >
                      Clear
                    </Button>
                  </Tooltip>
                )}
              </Group>
            </Stack>
          ) : (
            <div>
              <div className='signature-field-signed'>
                <SignatureCanvas
                  backgroundColor='#fff'
                  penColor='black'
                  canvasProps={{
                    className: 'signature-canvas',
                  }}
                  ref={sigCanvas as any}
                />
              </div>
              <p>
                Signed by {specificClientName} on {timeStamp}
              </p>
            </div>
          )}
        </div>
        <SaveDialog
          signatureStatus={signatureStatus}
          pctSaved={pctSaved}
          onCancelClick={handleCancelClick}
          onSaveACopyClick={handleSaveACopyClick}
        />
      </div>
    </>
  );
};

export default SignEngagementLetter;
